// Export a section or inline SVG of the (same-origin) doc as a PNG (with a
// background or transparent) or, for SVGs, as a standalone .svg. Targets are
// enumerated from the live iframe document so no hover-picking over the modal is
// needed. Every export carries the CONFIDENTIAL marking.

import { useMemo, useState } from "react";
import { Modal } from "./Modal";
import { Icon } from "./Icon";
import { Spinner } from "./Spinner";
import { useToast } from "./Toast";
import {
  exportNodePng,
  exportSvg,
  listSvgs,
  type ExportBackground,
} from "../export/image";

interface Target {
  key: string;
  label: string;
  node: HTMLElement | SVGSVGElement;
  isSvg: boolean;
}

interface Props {
  getDoc: () => Document | null;
  title: string;
  onClose: () => void;
}

function enumerateTargets(doc: Document): Target[] {
  const targets: Target[] = [];
  if (doc.body) {
    targets.push({ key: "doc", label: "Entire document", node: doc.body, isSvg: false });
  }
  const seen = new Set<Element>();
  const blocks = Array.from(
    doc.querySelectorAll<HTMLElement>("section, article, [data-slide], main > *, [id]")
  );
  for (const el of blocks) {
    if (seen.has(el) || el.tagName === "SVG") continue;
    const w = el.clientWidth;
    const h = el.clientHeight;
    if (w * h < 6000) continue; // skip tiny/inline elements
    seen.add(el);
    const heading = el.querySelector("h1,h2,h3")?.textContent?.trim();
    const label =
      (el.id && `#${el.id}`) ||
      (heading && heading.slice(0, 50)) ||
      el.tagName.toLowerCase();
    targets.push({ key: `sec-${targets.length}`, label, node: el, isSvg: false });
    if (targets.length > 40) break;
  }
  listSvgs(doc).forEach((svg, i) => {
    const w = Math.round(svg.clientWidth || svg.viewBox?.baseVal?.width || 0);
    const h = Math.round(svg.clientHeight || svg.viewBox?.baseVal?.height || 0);
    targets.push({
      key: `svg-${i}`,
      label: `Graphic ${i + 1}${w && h ? ` (${w}×${h})` : ""}`,
      node: svg,
      isSvg: true,
    });
  });
  return targets;
}

export function ImageExportDialog({ getDoc, title, onClose }: Props) {
  const targets = useMemo(() => {
    const doc = getDoc();
    return doc ? enumerateTargets(doc) : [];
  }, [getDoc]);

  const [selected, setSelected] = useState<string>(targets[0]?.key ?? "");
  const [background, setBackground] = useState<ExportBackground>("transparent");
  const [scale, setScale] = useState(2);
  const [format, setFormat] = useState<"png" | "svg">("png");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { success } = useToast();

  const current = targets.find((t) => t.key === selected) ?? null;
  const canSvg = current?.isSvg ?? false;

  const baseName = `${title}-${current?.label ?? "export"}`.replace(/[#\s]+/g, "-");

  const run = async () => {
    if (!current) return;
    setBusy(true);
    setError(null);
    try {
      if (format === "svg" && current.isSvg) {
        exportSvg(current.node as SVGSVGElement, baseName);
      } else {
        await exportNodePng(current.node, { background, scale, name: baseName });
      }
      success("Export ready — check your downloads.");
      onClose();
    } catch (e) {
      setError((e as Error).message || "Export failed.");
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Export image"
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={run}
            disabled={busy || !current}
          >
            {busy ? <Spinner /> : <Icon name="download" size={18} />}
            {busy ? "Exporting…" : "Export"}
          </button>
        </>
      }
    >
      {targets.length === 0 ? (
        <p className="empty-state">Nothing exportable was found in this document.</p>
      ) : (
        <>
          <div className="field">
            <span className="field-label">What to export</span>
            <ul className="picker-list picker-list--scroll">
              {targets.map((t) => (
                <li key={t.key}>
                  <button
                    type="button"
                    className={`picker-row${selected === t.key ? " is-selected" : ""}`}
                    onClick={() => {
                      setSelected(t.key);
                      if (!t.isSvg) setFormat("png");
                    }}
                  >
                    <Icon name={t.isSvg ? "image" : "file"} size={16} />
                    <span>{t.label}</span>
                    {selected === t.key && (
                      <Icon name="check" size={16} className="picker-check" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="export-options">
            {canSvg && (
              <label className="field field--inline">
                <span className="field-label">Format</span>
                <select
                  className="input"
                  value={format}
                  onChange={(e) => setFormat(e.target.value as "png" | "svg")}
                >
                  <option value="png">PNG (raster)</option>
                  <option value="svg">SVG (vector)</option>
                </select>
              </label>
            )}
            {format === "png" && (
              <>
                <label className="field field--inline">
                  <span className="field-label">Background</span>
                  <select
                    className="input"
                    value={background}
                    onChange={(e) => setBackground(e.target.value as ExportBackground)}
                  >
                    <option value="transparent">Transparent</option>
                    <option value="white">White</option>
                    <option value="cream">Brand cream</option>
                  </select>
                </label>
                <label className="field field--inline">
                  <span className="field-label">Scale</span>
                  <select
                    className="input"
                    value={scale}
                    onChange={(e) => setScale(Number(e.target.value))}
                  >
                    <option value={1}>1×</option>
                    <option value={2}>2×</option>
                    <option value={3}>3×</option>
                  </select>
                </label>
              </>
            )}
          </div>
        </>
      )}

      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
